import React, { useState, useEffect, useRef } from 'react';

const IndividualCourtScreen = () => {
  const [selectedCourt, setSelectedCourt] = useState('court1');
  const [liveMatchesByGroup, setLiveMatchesByGroup] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const pollingIntervalRef = useRef(null);
  const sideLabelsCache = useRef({});

  const API_BASE_URL = 'https://api.asterickx.com/api/MatchScoring';
  const API_ALL_LIVE = `${API_BASE_URL}/GetAllLiveScore`;
  const TOURNAMENT_ID = '7f479cd9-9169-488b-8f06-dc57b6ab64b1';
  const API_SIDE_LABELS = `${API_BASE_URL}/tournament/${TOURNAMENT_ID}/side-labels`;

  const courts = [
    { id: 'court1', name: 'Court 1', courtNumber: '1' },
    { id: 'court2', name: 'Court 2', courtNumber: '2' },
    { id: 'court3', name: 'Court 3', courtNumber: '3' },
    { id: 'court4', name: 'Court 4', courtNumber: '4' },
  ];

  const parseSideDisplay = (displayString) => {
    if (!displayString) return { players: [], state: '' };
    
    const stateMatch = displayString.match(/\(([^)]+)\)\s*$/);
    const state = stateMatch ? stateMatch[1] : '';
    
    const namesOnly = displayString.replace(/\s*\([^)]+\)\s*$/, '').trim();
    
    let players = [];
    if (namesOnly.includes(' & ')) {
      players = namesOnly.split(' & ').map(p => p.trim());
    } else if (namesOnly.includes(' / ')) {
      players = namesOnly.split(' / ').map(p => p.trim());
    } else {
      players = [namesOnly];
    }
    
    return { players, state };
  };

  const fetchTournamentSideLabels = async () => {
    try {
      const response = await fetch(API_SIDE_LABELS);
      
      if (!response.ok) {
        return {};
      }
      
      const data = await response.json();
      
      const labelsMap = {};
      if (Array.isArray(data)) {
        data.forEach(item => {
          if (item.matchId) {
            labelsMap[item.matchId] = item;
          }
        });
      }
      
      return labelsMap;
    } catch (error) {
      console.error('Error fetching tournament side labels:', error);
      return {};
    }
  };

  const fetchMatchDetails = async (matchId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${matchId}`);
      
      if (!response.ok) {
        console.warn(`Could not fetch details for match ${matchId}`);
        return null;
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error fetching match ${matchId}:`, error);
      return null;
    }
  };

  const getFirstName = (fullName) => {
    if (!fullName) return '';
    return fullName.trim().split(' ')[0];
  };

  const parsePlayerNames = (nameString) => {
    if (!nameString) return [];
    
    let players = [];
    
    if (nameString.includes(' & ')) {
      players = nameString.split(' & ').map(p => getFirstName(p));
    } else if (nameString.includes(' / ')) {
      players = nameString.split(' / ').map(p => getFirstName(p));
    } else if (nameString.includes('/')) {
      players = nameString.split('/').map(p => getFirstName(p));
    } else {
      players = [getFirstName(nameString)];
    }
    
    return players.filter(p => p.length > 0);
  };

  const convertApiDataToMatch = (apiData, liveData, sideLabelsData = null) => {
    const matchData = apiData || liveData;
    const sets = matchData.sets || [];
    
    // Determine current set number
    let currentSetNumber = 1;
    if (liveData?.currentSetNumber) {
      currentSetNumber = liveData.currentSetNumber;
    } else if (matchData?.currentSetNumber) {
      currentSetNumber = matchData.currentSetNumber;
    } else {
      for (let i = sets.length - 1; i >= 0; i--) {
        const set = sets[i];
        const s1 = set.side1Score || 0;
        const s2 = set.side2Score || 0;
        
        const isComplete = (s1 >= 21 && s1 - s2 >= 2) || 
                           (s2 >= 21 && s2 - s1 >= 2) ||
                           (s1 >= 30 || s2 >= 30);
        
        if (!isComplete && (s1 > 0 || s2 > 0)) {
          currentSetNumber = i + 1;
          break;
        } else if (isComplete) {
          currentSetNumber = i + 2;
          break;
        }
      }
      if (currentSetNumber > 3) currentSetNumber = 3;
    }

    let p1A = "", p1B = "", p2A = "", p2B = "";
    let side1State = "", side2State = "";
    
    if (sideLabelsData) {
      const side1Parts = parseSideDisplay(sideLabelsData.side1Display);
      const side2Parts = parseSideDisplay(sideLabelsData.side2Display);

      p1A = side1Parts.players[0] || "";
      p1B = side1Parts.players[1] || "";
      p2A = side2Parts.players[0] || "";
      p2B = side2Parts.players[1] || "";
      side1State = side1Parts.state || "";
      side2State = side2Parts.state || "";
    }

    const isDoubles = matchData.categoryName?.toLowerCase().includes('doubles') || 
                      (p1B && p1B.trim() !== "") || (p2B && p2B.trim() !== "");
    
    const side1Name = matchData.side1Label || 'Player 1';
    const side2Name = matchData.side2Label || 'Player 2';

    let side1Players = [];
    let side2Players = [];

    if (p1A && p1A.trim() !== "") {
      side1Players.push(getFirstName(p1A));
    }
    if (p1B && p1B.trim() !== "") {
      side1Players.push(getFirstName(p1B));
    }
    if (p2A && p2A.trim() !== "") {
      side2Players.push(getFirstName(p2A));
    }
    if (p2B && p2B.trim() !== "") {
      side2Players.push(getFirstName(p2B));
    }

    if (side1Players.length === 0) {
      side1Players = parsePlayerNames(side1Name);
    }
    if (side2Players.length === 0) {
      side2Players = parsePlayerNames(side2Name);
    }
    
    const side1Scores = [0, 0, 0];
    const side2Scores = [0, 0, 0];
    
    sets.forEach((set, index) => {
      if (index < 3) {
        side1Scores[index] = set.side1Score || 0;
        side2Scores[index] = set.side2Score || 0;
      }
    });

    const courtNumber = liveData?.courtNumber || '1';
    
    if (isDoubles || side1Players.length > 1 || side2Players.length > 1) {
      return {
        id: matchData.matchId || liveData.matchId,
        matchType: `${matchData.categoryName} - ${matchData.roundName}`,
        courtNumber: courtNumber,
        team1: {
          players: side1Players,
          state: side1State,
          scores: side1Scores,
          serving: matchData.currentServerSide === 1
        },
        team2: {
          players: side2Players,
          state: side2State,
          scores: side2Scores,
          serving: matchData.currentServerSide === 2
        },
        currentSet: currentSetNumber,
        status: matchData.status || liveData?.matchStatus,
        isDoubles: true
      };
    } else {
      return {
        id: matchData.matchId || liveData.matchId,
        matchType: `${matchData.categoryName} - ${matchData.roundName}`,
        courtNumber: courtNumber,
        player1: {
          name: side1Players[0] || side1Name,
          state: side1State,
          scores: side1Scores,
          serving: matchData.currentServerSide === 1
        },
        player2: {
          name: side2Players[0] || side2Name,
          state: side2State,
          scores: side2Scores,
          serving: matchData.currentServerSide === 2
        },
        currentSet: currentSetNumber,
        status: matchData.status || liveData?.matchStatus,
        isDoubles: false
      };
    }
  };

  const fetchAllMatches = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      
      const sideLabelsMap = await fetchTournamentSideLabels();
      sideLabelsCache.current = sideLabelsMap;
      
      const response = await fetch(API_ALL_LIVE);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const allMatchesData = await response.json();
      
      if (!Array.isArray(allMatchesData) || allMatchesData.length === 0) {
        setLiveMatchesByGroup({});
        if (!silent) setLoading(false);
        setLastUpdate(new Date().toLocaleTimeString());
        return;
      }
      
      const inProgressMatches = allMatchesData.filter(match => match.matchStatus === "In Progress");
      
      const detailedLiveMatches = await Promise.all(
        inProgressMatches.map(async (liveMatch) => {
          const details = await fetchMatchDetails(liveMatch.matchId);
          const sideLabels = sideLabelsMap[liveMatch.matchId] || null;
          return convertApiDataToMatch(details, liveMatch, sideLabels);
        })
      );
      
      const liveByGroup = {};
      detailedLiveMatches.forEach(match => {
        const courtKey = `court${match.courtNumber}`;
        liveByGroup[courtKey] = match;
      });
      
      setLiveMatchesByGroup(liveByGroup);
      setLastUpdate(new Date().toLocaleTimeString());
      if (!silent) setLoading(false);
    } catch (error) {
      console.error('Error fetching matches:', error);
      setError(error.message);
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllMatches();

    pollingIntervalRef.current = setInterval(() => {
      fetchAllMatches(true);
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const currentLiveMatch = liveMatchesByGroup[selectedCourt];

  const formatPlayerName = (participant) => {
    if (participant.players) {
      return participant.players.join(' & ');
    }
    return participant.name;
  };

  const getSetWinner = (match, setIndex) => {
    if (!match) return null;
    const p1Score = match.isDoubles ? match.team1.scores[setIndex] : match.player1.scores[setIndex];
    const p2Score = match.isDoubles ? match.team2.scores[setIndex] : match.player2.scores[setIndex];
    
    if (p1Score === undefined || p2Score === undefined) return null;
    if (p1Score >= 21 && p1Score - p2Score >= 2) return 1;
    if (p2Score >= 21 && p2Score - p1Score >= 2) return 2;
    if (p1Score >= 30 || p2Score >= 30) return p1Score > p2Score ? 1 : 2;
    return null;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        background: 'white',
        padding: '20px 30px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          <div style={{ flex: '1', minWidth: '250px' }}>
            <h1 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#333',
              margin: 0
            }}>
              Yonex-Sunrise 4th Mini National Badminton Championship-2025
            </h1>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '14px', color: '#666' }}>Select Court:</label>
              <select
                style={{
                  padding: '8px 15px',
                  fontSize: '14px',
                  border: '2px solid #667eea',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#333',
                  cursor: 'pointer',
                  outline: 'none'
                }}
                value={selectedCourt}
                onChange={(e) => setSelectedCourt(e.target.value)}
              >
                {courts.map((court) => (
                  <option key={court.id} value={court.id}>
                    {court.name}
                  </option>
                ))}
              </select>
            </div>
            <button 
              onClick={() => fetchAllMatches(false)} 
              disabled={loading}
              style={{
                padding: '8px 15px',
                fontSize: '18px',
                border: 'none',
                borderRadius: '6px',
                background: '#667eea',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? '⏳' : '🔄'}
            </button>
            {lastUpdate && (
              <span style={{
                fontSize: '13px',
                color: '#666'
              }}>
                Updated: {lastUpdate}
              </span>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div style={{ 
          background: '#fee', 
          border: '1px solid #fcc', 
          padding: '15px', 
          borderRadius: '8px', 
          margin: '20px',
          color: '#c33',
          maxWidth: '1400px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Main Content */}
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '40px 30px'
      }}>
        {/* Court Name Banner */}
        <div style={{
          background: 'white',
          padding: '20px 30px',
          borderRadius: '12px 12px 0 0',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          marginBottom: '0'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#667eea',
            textAlign: 'center'
          }}>
            {courts.find((c) => c.id === selectedCourt)?.name}
          </div>
        </div>

        {/* Live Match Section */}
        <section style={{
          background: 'white',
          borderRadius: '0 0 12px 12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          padding: '30px',
          minHeight: '500px'
        }}>
          {currentLiveMatch ? (
            <div>
              {/* Match Type */}
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '15px 25px',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: 'bold',
                textAlign: 'center',
                marginBottom: '30px'
              }}>
                {currentLiveMatch.matchType}
              </div>

              {/* Scoreboard */}
              <div>
                {/* Player 1 / Team 1 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '25px',
                  borderBottom: '2px solid #e0e0e0',
                  background: (currentLiveMatch.isDoubles ? currentLiveMatch.team1.serving : currentLiveMatch.player1.serving) 
                    ? '#fff9e6' : 'transparent'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    flex: 1
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '28px',
                      fontWeight: 'bold',
                      color: '#333',
                      flexShrink: 0
                    }}>
                      {(currentLiveMatch.isDoubles 
                        ? currentLiveMatch.team1.players[0] 
                        : currentLiveMatch.player1.name
                      ).charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        marginBottom: '5px'
                      }}>
                        {currentLiveMatch.isDoubles 
                          ? formatPlayerName(currentLiveMatch.team1)
                          : currentLiveMatch.player1.name}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        {currentLiveMatch.isDoubles 
                          ? currentLiveMatch.team1.state 
                          : currentLiveMatch.player1.state}
                      </div>
                    </div>
                    {(currentLiveMatch.isDoubles ? currentLiveMatch.team1.serving : currentLiveMatch.player1.serving) && (
                      <div style={{
                        fontSize: '24px',
                        marginRight: '10px'
                      }}>
                        🏸
                      </div>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '15px'
                  }}>
                    {[0, 1, 2].map((setIndex) => {
                      const score = currentLiveMatch.isDoubles 
                        ? currentLiveMatch.team1.scores[setIndex] 
                        : currentLiveMatch.player1.scores[setIndex];
                      const isCurrentSet = currentLiveMatch.currentSet === setIndex + 1;
                      const setWinner = getSetWinner(currentLiveMatch, setIndex);
                      
                      return (
                        <div
                          key={setIndex}
                          style={{
                            width: '80px',
                            height: '80px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '32px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            background: isCurrentSet ? '#ffd700' : setWinner === 1 ? '#4caf50' : '#f5f5f5',
                            color: isCurrentSet ? '#000' : setWinner === 1 ? '#fff' : '#999',
                            border: '2px solid',
                            borderColor: isCurrentSet ? '#ffc107' : setWinner === 1 ? '#4caf50' : '#e0e0e0'
                          }}
                        >
                          {score !== undefined && score > 0 ? score : '-'}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Player 2 / Team 2 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '25px',
                  background: (currentLiveMatch.isDoubles ? currentLiveMatch.team2.serving : currentLiveMatch.player2.serving) 
                    ? '#fff9e6' : 'transparent'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    flex: 1
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '28px',
                      fontWeight: 'bold',
                      color: 'white',
                      flexShrink: 0
                    }}>
                      {(currentLiveMatch.isDoubles 
                        ? currentLiveMatch.team2.players[0] 
                        : currentLiveMatch.player2.name
                      ).charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        marginBottom: '5px'
                      }}>
                        {currentLiveMatch.isDoubles 
                          ? formatPlayerName(currentLiveMatch.team2)
                          : currentLiveMatch.player2.name}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        {currentLiveMatch.isDoubles 
                          ? currentLiveMatch.team2.state 
                          : currentLiveMatch.player2.state}
                      </div>
                    </div>
                    {(currentLiveMatch.isDoubles ? currentLiveMatch.team2.serving : currentLiveMatch.player2.serving) && (
                      <div style={{
                        fontSize: '24px',
                        marginRight: '10px'
                      }}>
                        🏸
                      </div>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '15px'
                  }}>
                    {[0, 1, 2].map((setIndex) => {
                      const score = currentLiveMatch.isDoubles 
                        ? currentLiveMatch.team2.scores[setIndex] 
                        : currentLiveMatch.player2.scores[setIndex];
                      const isCurrentSet = currentLiveMatch.currentSet === setIndex + 1;
                      const setWinner = getSetWinner(currentLiveMatch, setIndex);
                      
                      return (
                        <div
                          key={setIndex}
                          style={{
                            width: '80px',
                            height: '80px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '32px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            background: isCurrentSet ? '#ffd700' : setWinner === 2 ? '#4caf50' : '#f5f5f5',
                            color: isCurrentSet ? '#000' : setWinner === 2 ? '#fff' : '#999',
                            border: '2px solid',
                            borderColor: isCurrentSet ? '#ffc107' : setWinner === 2 ? '#4caf50' : '#e0e0e0'
                          }}
                        >
                          {score !== undefined && score > 0 ? score : '-'}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              color: '#999'
            }}>
              <div style={{ fontSize: '80px', marginBottom: '20px' }}>🏸</div>
              <h2 style={{ fontSize: '28px', color: '#666', marginBottom: '10px' }}>No Live Match</h2>
              <p style={{ fontSize: '16px' }}>
                {loading && !lastUpdate ? 'Loading matches...' : 'There is no match currently being played on this court'}
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default IndividualCourtScreen;